import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ServicesSection = () => {
  const services = [
    {
      icon: 'Video',
      title: 'HD Video Streaming',
      description: 'Crystal-clear 1080p video quality with adaptive bitrate streaming for seamless webinar experiences worldwide.',
      number: '01'
    },
    {
      icon: 'Users',
      title: 'Unlimited Attendees',
      description: 'Scale from intimate team meetings to massive global events without capacity limits or quality compromise.',
      number: '02'
    },
    {
      icon: 'MessageSquare',
      title: 'Interactive Features',
      description: 'Engage audiences with live polls, Q&A sessions, breakout rooms, and real-time chat functionality.',
      number: '03'
    },
    {
      icon: 'BarChart',
      title: 'Advanced Analytics',
      description: 'Track attendance, engagement metrics, and participant behavior with comprehensive dashboard insights.',
      number: '04'
    },
    {
      icon: 'Download',
      title: 'Auto Recording',
      description: 'Automatic cloud recording with instant playback, transcription, and secure content management.',
      number: '05'
    },
    {
      icon: 'Globe',
      title: 'Global CDN',
      description: 'Lightning-fast content delivery across 200+ countries with enterprise-grade reliability and uptime.',
      number: '06'
    }
  ];

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-500 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-blue-100 rounded-full mb-6">
            <Icon name="Zap" size={18} className="text-blue-600" />
            <span className="text-sm font-bold text-blue-900">Platform Features</span>
          </div>

          <h2 className="text-5xl font-black mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Everything You Need
            </span>
          </h2>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Professional webinar tools designed for modern teams and organizations
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              whileHover={{ y: -8 }}
              className="relative group"
            >
              {/* Card */}
              <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-3xl border-2 border-blue-100 hover:border-blue-300 transition-all shadow-lg hover:shadow-2xl">
                
                {/* Number Badge */}
                <div className="absolute -top-4 -right-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center text-white font-black text-lg shadow-xl transform rotate-12 group-hover:rotate-0 transition-transform">
                    {service.number}
                  </div>
                </div>

                {/* Icon */}
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 360 }}
                  transition={{ duration: 0.6 }}
                  className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg"
                >
                  <Icon name={service.icon} size={32} className="text-white" />
                </motion.div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                  {service.title}
                </h3>

                <p className="text-gray-600 leading-relaxed mb-4">
                  {service.description}
                </p>

                {/* Arrow */}
                <div className="flex items-center text-blue-600 font-semibold text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                  <span>Explore Feature</span>
                  <Icon name="ArrowRight" size={16} className="ml-2" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="text-center mt-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl p-12 text-white"
        >
          <h3 className="text-3xl font-black mb-4">Ready to Get Started?</h3>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of organizations hosting successful webinars with DailyRespond
          </p>
          <button className="bg-white text-blue-600 hover:bg-blue-50 font-bold px-10 py-4 rounded-full transition-all shadow-xl inline-flex items-center gap-2">
            Start Free Trial
            <Icon name="ArrowRight" size={20} />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
